import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild, TemplateRef, computed, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { DropdownComponent, DropdownOption } from "../../../shared/components/ui/dropdown/dropdown.component";
import { ButtonComponent } from "../../../shared/components/ui/button/button.component";
import { ApiService } from '../../../core/services/api.service';
import { PaginatedTableComponent, TableHeader } from "../../../shared/components/tables/paginated-table/paginated-table.component";
import { AlertComponent } from "../../../shared/components/alert/alert.component";
import { IconComponent, IconName } from '../../../shared/icons/icon.component';
import { ToasterService } from '../../../core/services/toaster.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { LoaderService } from '../../../core/services/loader.service';
import { SlidePanelComponent } from '../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../shared/components/ui/input/input.component';
import { ToggleComponent } from '../../../shared/components/ui/toggle/toggle.component';
import { AuditLogPanelComponent } from '../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../core/models/audit-log.model';

interface Release {
  id: string;
  platforms: string[];
  type: string;
  currentVersion: string;
  versionName: string;
  availableTo: string[];
  releaseDate: string;
  releaseTitle: string;
  isBreakingChange: boolean;
  description: string;
  linkTags?: { label: string; url: string }[];
  notes?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ReleaseApiResponse {
  releases: Release[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

@Component({
  selector: 'app-release-document',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    DropdownComponent,
    ButtonComponent,
    PaginatedTableComponent,
    SlidePanelComponent,
    InputComponent,
    IconComponent,
    AuditLogPanelComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './release-document.component.html'
})
export class ReleaseDocumentComponent implements OnInit {
  @ViewChild('dateDropdown') dateDropdown!: DropdownComponent;
  @ViewChild('releaseDetailTemplate') releaseDetailTemplate!: TemplateRef<any>;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  private fb = inject(FormBuilder);
  loaderService = inject(LoaderService);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);
  private apiservice = inject(ApiService);
  router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  // States
  panelOpen = signal(false);
  selectedRelease = signal<any>(null);
  dialogRef = signal<MatDialogRef<any> | null>(null);
  isLoading = signal(false);
  formSubmitted = signal(false);
  errorMessage = signal('');
  successMessage = signal('');
  totalItems = signal(0);
  isEditing = signal(false);
  editingId = signal<string | null>(null);

  auditEntityTypes = [AuditEntityType.RELEASE];

  // Filters
  searchTerm = signal('');
  selectedPlatform = signal<string | null>(null);
  selectedAvailableTo = signal<string | null>(null);
  selectedProduct = signal<number | null>(null);
  page = signal(1);
  pageSize = signal(10);
  sortBy = signal('createdAt');
  sortOrder = signal('');
  releaseData = signal<Release[]>([]);
  latestReleaseVersion = computed(() => {
    const releases = this.releaseData();
    return releases.length > 0 ? releases[0].versionName : 'N/A';
  });

  // Links and Tags
  newLink = signal('');
  newTag = signal('');
  links = signal<string[]>([]);
  tags = signal<string[]>([]);

  // Products
  productOptions = signal<DropdownOption[]>([]);

  // Form
  form: FormGroup;

  // Table Headers
  tableHeaders: TableHeader[] = [
    { label: 'Version', key: 'versionName', align: 'text-left' },
    { label: 'Title', key: 'releaseTitle', align: 'text-left' },
    { label: 'Description', key: 'descriptionHtml', align: 'text-left', isHtml: true },
    { label: 'Type & Platforms', key: 'typePlatformsHtml', align: 'text-left', isHtml: true },
    { label: 'Release Date', key: 'releaseDate', align: 'text-left', dateFormat: 'shortDate' },
  ];

  tableActionOptions = [
    { label: 'Edit', value: 'edit', iconName: 'pencil' as IconName, isShow: true, visibilityKey: 'isNotDeleted', color: '#AD46FF' },
    { label: 'Delete', value: 'delete', iconName: 'trash' as IconName, isShow: true, visibilityKey: 'isNotDeleted', color: '#EF4444' },
    { label: 'View Details', value: 'view', iconName: 'eye' as IconName, isShow: true, color: '#1E6AFD' }
  ];

  // User Dropdown Options
  platformOptions = [
    { value: 'WEB', label: 'Web', iconName: 'globe' as IconName },
    { value: 'ANDROID', label: 'Android', iconName: 'smartphone' as IconName },
    { value: 'IOS', label: 'iOS', iconName: 'smartphone' as IconName },
    { value: 'ADMIN', label: 'Admin', iconName: 'settings' as IconName }
  ];

  typeOptions = [
    { value: 'MAJOR', label: 'Major' },
    { value: 'MINOR', label: 'Minor' },
    { value: 'PATCH', label: 'Patch' }
  ];

  availableToOptions = [
    { value: 'SUPER_ADMIN', label: 'Super Admin' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'MANAGERS', label: 'Managers' },
    { value: 'SUPPORT_AGENTS', label: 'Support Agents' }
  ];

  loadProducts() {
    this.apiservice.fetchProducts().subscribe({
      next: (products) => {
        const uniqueProducts = Array.from(
          new Map(products.map((p) => [p.id, p])).values()
        );
        this.productOptions.set(
          uniqueProducts.map((p) => ({
            value: p.pid,
            label: p.name || p.displayName || p.label,
          }))
        );
      },
    });
  }

  getPlatformLabel(value: string) {
    return this.platformOptions.find(opt => opt.value === value)?.label || value;
  }

  getTypeLabel(value: string) {
    return this.typeOptions.find(opt => opt.value === value)?.label || value;
  }

  constructor() {
    this.form = this.fb.group({
      platforms: ['WEB', [Validators.required]],
      type: ['MINOR', [Validators.required]],
      productId: ['', [Validators.required]],
      versionName: ['', [Validators.required]],
      availableTo: [['SUPER_ADMIN'], [Validators.required]],
      releaseDate: [new Date().toISOString().split('T')[0], [Validators.required]],
      releaseTitle: ['', [Validators.required]],
      isBreakingChange: [false],
      description: ['', [Validators.required]],
      notes: ['']
    });
  }

  get processedReleaseData() {
    return this.releaseData().map(release => {
      const typeLabel = release.type.charAt(0).toUpperCase() + release.type.slice(1).toLowerCase();
      const typeClasses = release.type === 'MAJOR' ? 'bg-[#F3E8FF] text-[#AD46FF]' :
        release.type === 'MINOR' ? 'bg-[#E0F2FE] text-[#0369A1]' : 'bg-[#F1F5F9] text-[#475569]';

      const platformsHtml = (release.platforms || []).map(p => {
        let iconPath = '';
        let color = '#64748b';
        if (p === 'WEB') {
          iconPath = '<path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/>';
          color = '#1E6AFD';
        } else if (p === 'ANDROID') {
          iconPath = '<path d="M3.5 13h17M7 9l-1.5-2M17 9l1.5-2M7 13a5 5 0 0 1 10 0"/>';
          color = '#84CC16';
        } else if (p === 'IOS') {
          iconPath = '<path d="M12 20.94c1.88 0 3.05-1.07 3.05-1.07s1.17 1.07 3.05 1.07 3.05-1.07 3.05-1.07-1.17-1.07-3.05-1.07-3.05 1.07-3.05 1.07-1.17-1.07-3.05-1.07-3.05 1.07-3.05 1.07z"/>';
          color = '#64748B';
        }

        return `<span class="inline-flex items-center gap-1.5 ml-2">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${iconPath}</svg>
                  <span class="text-[11px] font-medium text-slate-600 dark:text-slate-400">${p.charAt(0) + p.slice(1).toLowerCase()}</span>
                </span>`;
      }).join('');

      const truncatedDesc = (release.description || '').length > 60
        ? release.description.substring(0, 60) + '...<span class="text-[#AD46FF] font-medium cursor-pointer">View More</span>'
        : (release.description || '');

      return {
        ...release,
        descriptionHtml: truncatedDesc,
        typePlatformsHtml: `<div class="flex items-center">
                              <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${typeClasses}">${typeLabel}</span>
                              ${platformsHtml}
                            </div>`,
        isDeleted: release.status === 'DELETED',
        isNotDeleted: release.status !== 'DELETED'
      };
    });
  }

  ngOnInit() {
    this.fetchReleases();
    this.loadProducts();
  }

  // Placeholder if needed for future lookups
  fetchRoles() {
    // Roles are no longer used for releases, keeping empty for now to avoid breaking other logic
  }

  // Panel Methods
  openPanel(release?: any) {
    if (release) {
      this.isEditing.set(true);
      this.editingId.set(release.id);
      this.form.patchValue({
        ...release,
        platforms: Array.isArray(release.platforms) ? release.platforms[0] : (release.platforms || release.platform),
        productId: release.productId || (release.productIds ? release.productIds[0] : null),
        releaseDate: release.releaseDate ? release.releaseDate.split('T')[0] : '',
        notes: release.notes || ''
      });
      this.links.set((release.linkTags || []).map((l: any) => l.url || l));
      this.tags.set((release.linkTags || []).map((l: any) => l.label || ''));
    } else {
      this.isEditing.set(false);
      this.editingId.set(null);
      this.form.patchValue({
        platforms: 'WEB',
        type: 'MINOR',
        versionName: '',
        productId: '',
        releaseTitle: '',
        isBreakingChange: false,
        description: '',
        availableTo: ['SUPER_ADMIN'],
        releaseDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      this.links.set([]);
      this.tags.set([]);
    }
    this.newLink.set('');
    this.newTag.set('');
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.formSubmitted.set(false);
    this.form.reset();
    this.links.set([]);
    this.tags.set([]);
  }

  // Form Submission
  submitForm() {
    this.formSubmitted.set(true);
    if (this.form.invalid) return;

    this.isLoading.set(true);

    const formValue = this.form.getRawValue();

    let payload: any = {
      versionName: formValue.versionName,
      releaseTitle: formValue.releaseTitle,
      releaseDate: formValue.releaseDate,
      status: 'DRAFT',
      type: formValue.type,
      isBreakingChange: formValue.isBreakingChange || false,
      description: formValue.description,
      notes: formValue.notes,

      // FIX APPLIED HERE
      linkTags: this.links().map((url, index) => ({
        label: this.tags()[index] || 'Link',
        url
      })),

      platforms: [formValue.platforms],
      availableTo: formValue.availableTo,
      productIds: [formValue.productId]
    };

    const endpoint = this.isEditing() ? `/releases/${this.editingId()}` : '/releases';
    const request = this.isEditing()
      ? this.apiservice.patch(endpoint, payload)
      : this.apiservice.post(endpoint, payload);

    request.subscribe({
      next: (response: any) => {
        this.isLoading.set(false);
        this.toasterService.success(response?.data?.message || `Release ${this.isEditing() ? 'updated' : 'saved'} successfully`, 'Success');
        this.closePanel();
        this.fetchReleases();
        this.auditLogPanel?.reloadLogs();
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toasterService.error(err?.message || `Error ${this.isEditing() ? 'updating' : 'saving'} release`, 'Error');
      }
    });
  }

  addLink() {
    const link = this.newLink().trim();
    if (link && !this.links().includes(link)) {
      this.links.set([...this.links(), link]);
      this.newLink.set('');
    }
  }

  removeLink(link: string) {
    const index = this.links().indexOf(link);
    this.links.set(this.links().filter(l => l !== link));
    if (index > -1) {
      this.tags.set(this.tags().filter((_, i) => i !== index));
    }
  }

  addTag() {
    const tag = this.newTag().trim();
    if (tag) {
      this.tags.set([...this.tags(), tag]);
      this.newTag.set('');
    }
  }

  removeTag(tag: string) {
    this.tags.set(this.tags().filter(t => t !== tag));
  }

  toggleAvailableTo(value: string) {
    const current = this.form.get('availableTo')?.value || [];
    if (current.includes(value)) {
      this.form.get('availableTo')?.setValue(current.filter((v: string) => v !== value));
    } else {
      this.form.get('availableTo')?.setValue([...current, value]);
    }
  }

  onInputChange(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      control.markAsUntouched();
      control.markAsPristine();
    }
  }

  // Space Handling Utilities
  preventLeadingSpace(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    if (event.key === ' ' && target.selectionStart === 0) {
      event.preventDefault();
    }
  }

  // trimLeadingControlValue(controlName: string): void {
  //   const control = this.form.get(controlName);
  //   if (control && typeof control.value === 'string' && control.value.startsWith(' ')) {
  //     control.setValue(control.value.trimStart(), { emitEvent: false });
  //   }
  // }

  // trimControlValue(controlName: string): void {
  //   const control = this.form.get(controlName);
  //   if (control && typeof control.value === 'string') {
  //     control.setValue(control.value.trim(), { emitEvent: false });
  //   }
  // }

  // trimFormStrings(group: FormGroup): void {
  //   Object.keys(group.controls).forEach((key) => {
  //     const control = group.get(key);
  //     if (control instanceof FormGroup) {
  //       this.trimFormStrings(control);
  //     } else if (typeof control?.value === 'string') {
  //       control.setValue(control.value.trim(), { emitEvent: false });
  //     }
  //   });
  // }

  fetchReleases(): void {
    this.isLoading.set(true);
    const params: any = {
      page: this.page(),
      limit: this.pageSize()
    };

    const platform = this.selectedPlatform();
    const search = this.searchTerm();
    const availableTo = this.selectedAvailableTo();
    const product = this.selectedProduct();
    const sortOrder = this.sortOrder();

    if (platform) params.platforms = platform;
    if (search) params.search = search;
    if (availableTo) params.availableTo = availableTo;
    if (product) params.productId = product;
    if (sortOrder) params.sortOrder = sortOrder;

    this.apiservice.get('/releases', params, {
      showLoader: true,
      showErrorToaster: true,
      showSuccessToaster: false,
    }).subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          this.releaseData.set(response);
          this.totalItems.set(response.length);
        } else {
          const apiResponse = response as ReleaseApiResponse;
          this.releaseData.set(apiResponse.releases || []);
          this.totalItems.set(apiResponse.pagination?.total || (apiResponse.releases?.length || 0));
        }
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.isLoading.set(false);
        this.toasterService.error(error?.error || error?.message || 'Failed to load release list', 'Release List', 2000);
      }
    });
  }



  // Filter Handlers
  onPlatformFilter(platform: string | string[] | null): void {
    const selectedId = Array.isArray(platform) ? platform[0] : platform;
    this.selectedPlatform.set(selectedId === '' ? null : selectedId);
  }

  onAvailableToFilter(availableTo: string | string[] | null): void {
    const selectedId = Array.isArray(availableTo) ? availableTo[0] : availableTo;
    this.selectedAvailableTo.set(selectedId === '' ? null : selectedId);
  }

  onProductFilter(productId: any): void {
    const selectedId = Array.isArray(productId) ? productId[0] : productId;
    this.selectedProduct.set(selectedId === '' || selectedId === null ? null : Number(selectedId));
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
  }

  applyFilters() {
    this.page.set(1);
    this.fetchReleases();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchReleases();
  }

  clearFilters() {
    this.selectedPlatform.set(null);
    this.selectedAvailableTo.set(null);
    this.selectedProduct.set(null);
    this.searchTerm.set('');
    this.page.set(1);
    this.fetchReleases();
  }

  exportToExcel() {
    const dataToExport = this.releaseData().map(release => ({
      Version: release.versionName,
      Title: release.releaseTitle,
      Status: release.status,
      Type: release.type,
      Platforms: (release.platforms || []).join(', '),
      'Release Date': release.releaseDate ? new Date(release.releaseDate).toLocaleDateString() : 'N/A',
      Description: release.description,
      'Available To': (release.availableTo || []).join(', '),
      Notes: release.notes || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Releases');

    // Auto-size columns
    const maxWidths = dataToExport.reduce((acc: any, row: any) => {
      Object.keys(row).forEach((key, i) => {
        const val = String(row[key]);
        acc[i] = Math.max(acc[i] || 0, val.length, key.length);
      });
      return acc;
    }, []);
    worksheet['!cols'] = maxWidths.map((w: number) => ({ wch: w + 2 }));

    XLSX.writeFile(workbook, `releases_export_${new Date().getTime()}.xlsx`);
    this.toasterService.success('Excel file downloaded successfully', 'Export');
  }

  // Event Handlers for Table
  selectedRowItem(item: any) {
    if (!item || !item.row) return;
    if (item.action === 'edit') {
      this.openPanel(item.row);
    } else if (item.action === 'view') {
      this.onViewDetails(item.row);
    } else if (item.action === 'delete') {
      this.openConfirmationDialog(item.row);
    } else if (item.action === 'restore') {
      this.handleRestoreRelease(item.row.id);
    }
  }

  onViewDetails(row: any) {
    this.selectedRelease.set(row);
    const ref = this.dialog.open(this.releaseDetailTemplate, {
      width: 'auto',
      panelClass: 'release-detail-dialog'
    });
    this.dialogRef.set(ref);
  }

  closeDialog() {
    this.dialogRef()?.close();
    this.dialogRef.set(null);
  }

  editFromDetails() {
    const data = this.selectedRelease();
    this.closeDialog();
    this.openPanel(data);
  }

  deleteFromDetails() {
    const data = this.selectedRelease();
    this.closeDialog();
    this.openConfirmationDialog(data);
  }

  openConfirmationDialog(row: any) {
    this.dialog.open(ConfirmationModalComponent, {
      width: '400px',
      data: { title: 'Confirm Deletion', description: `Are you sure you want to delete this release?` }
    }).afterClosed().subscribe(res => {
      if (res) this.handleRemoveRelease(row.id);
    });
  }

  handleRemoveRelease(id: string) {
    this.apiservice.delete(`/releases/${id}`, {
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: true
    }).subscribe({
      next: () => {
        this.toasterService.success('Release removed successfully', 'Success');
        this.fetchReleases();
        this.auditLogPanel?.reloadLogs();
      },
      error: (error) => this.errorMessage.set(error?.error || error?.message || 'Error removing release')
    });
  }

  handleRestoreRelease(id: string) {
    this.apiservice.post(`/releases/${id}/restore`, {}, {
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: true
    }).subscribe({
      next: () => {
        this.toasterService.success('Release restored successfully', 'Success');
        this.fetchReleases();
        this.auditLogPanel?.reloadLogs();
      },
      error: (error) => this.errorMessage.set(error?.error || error?.message || 'Error restoring release')
    });
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (!control || ((!control.touched && !this.formSubmitted()) || control.valid)) return undefined;

    if (control.hasError('required')) return 'This field is required';
    if (control.hasError('email')) return 'Please enter a valid email address';
    if (control.hasError('minlength')) {
      const actual = control.errors?.['minlength']?.actualLength;
      const required = control.errors?.['minlength']?.requiredLength;
      return `Minimum ${required} characters required (current: ${actual})`;
    }
    if (control.hasError('maxlength')) {
      const actual = control.errors?.['maxlength']?.actualLength;
      const required = control.errors?.['maxlength']?.requiredLength;
      return `Maximum ${required} characters allowed (current: ${actual})`;
    }
    if (control.hasError('pattern')) {
      if (controlName === 'firstName' || controlName === 'lastName') return 'Alphabetic characters only';
      if (controlName === 'phoneNumber') return 'Digits only';
      if (controlName === 'avatarUrl') return 'Must be a valid URL starting with http:// or https://';
      if (controlName === 'password') return 'Must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
      return 'Invalid format';
    }

    return undefined;
  }

  openAddUserPanel() { this.openPanel(); }
}
